import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ref, onValue, get } from "../lib/firebase-database";
import { db } from "../firebase";
import { Layout } from "./Layout";
import { useAuth } from "../hooks/useAuth";
import { FileText, ArrowLeft, Search, X } from 'lucide-react';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { QRCodeSVG } from 'qrcode.react';
import { PDFDocument, rgb } from 'pdf-lib';
import { InvoiceGenerator } from './InvoiceGenerator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

export function BillingPage() {
  const [bookings, setBookings] = useState([]);
  const [monthlyBillData, setMonthlyBillData] = useState([]);
  const [totalBillAmount, setTotalBillAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchQuery, setSearchQuery] = useState("");
  const [showDetails, setShowDetails] = useState(null);
  const [showBillDialog, setShowBillDialog] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const adminUPI = "admin@upi"; // Replace with your actual UPI ID
const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);

  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate("/");
      return;
    }
  }, [user, isAdmin, navigate]);

  useEffect(() => {
    const allBookingsRef = ref(db, "bookings");
    const unsubscribe = onValue(allBookingsRef, async (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setBookings([]);
        setLoading(false);
        return;
      }

      try {
        const villasRef = ref(db, "villas");
        const villasSnapshot = await get(villasRef);
        const villasData = villasSnapshot.val() || {};

        // Use a Set to track processed booking IDs
        const processedBookingIds = new Set();
        const allBookings = [];
        
        for (const [userId, userBookings] of Object.entries(data)) {
          if (!userBookings) continue;

          for (const [bookingId, booking] of Object.entries(userBookings)) {
            // Skip if this booking ID has already been processed
            if (processedBookingIds.has(bookingId)) continue;
            
            // Add the booking ID to processed set
            processedBookingIds.add(bookingId);

            const villaData = villasData[booking.villaId] || {};
            const ownerData = villaData.owner || {};

            allBookings.push({
              id: bookingId,
              bookingId: bookingId, // Ensure bookingId is consistently set
              villaId: booking.villaId,
              userId: userId,
              villaName: villaData.name || "Villa Not Found",
              ownerName: ownerData.name || "Owner Not Found",
              ownerEmail: ownerData.email || "",
              ownerUPI: ownerData.upiId || "",
              ...booking
            });
          }
        }

        // Sort bookings by check-in date
        const sortedBookings = allBookings.sort((a, b) => {
          return new Date(a.checkIn) - new Date(b.checkIn);
        });

        setBookings(sortedBookings);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!bookings.length) {
      setMonthlyBillData([]);
      setTotalBillAmount(0);
      return;
    }

    const filteredBookings = bookings.filter(booking => {
      if (!booking.checkIn) return false;
      const checkInDate = new Date(booking.checkIn);
      return (
        checkInDate.getMonth() === selectedMonth &&
        checkInDate.getFullYear() === selectedYear &&
        booking.status !== "cancelled"
      );
    });

    // Group bookings by villa using a Map to ensure unique bookings
    const villaBillData = new Map();
    
    filteredBookings.forEach(booking => {
      const villaKey = `${booking.villaId}-${booking.villaName}`;
      
      if (!villaBillData.has(villaKey)) {
        villaBillData.set(villaKey, {
          villaId: booking.villaId,
          villaName: booking.villaName,
          ownerName: booking.ownerName,
          ownerUPI: booking.ownerUPI,
          totalBill: 0,
          bookings: new Map(), // Use Map to ensure unique bookings
        });
      }
      
      const villaData = villaBillData.get(villaKey);
      // Only add booking if it hasn't been processed yet
      if (!villaData.bookings.has(booking.bookingId)) {
        const amount = booking.guests * 100;
        villaData.totalBill += amount;
        villaData.bookings.set(booking.bookingId, {
          ...booking,
          billAmount: amount
        });
      }
    });

    // Convert Map data structure back to array format
    const billData = Array.from(villaBillData.values())
      .map(villa => ({
        ...villa,
        bookings: Array.from(villa.bookings.values())
      }))
      .filter(villa => villa.totalBill > 0);

    setMonthlyBillData(billData);
    setTotalBillAmount(billData.reduce((sum, villa) => sum + villa.totalBill, 0));
  }, [bookings, selectedMonth, selectedYear]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}, ${String(date.getFullYear()).slice(-2)}`;
  };

  const handleBack = () => {
    navigate("/admin/bookings");
  };

  const filteredVillas = monthlyBillData.filter(villa =>
    villa.villaName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    villa.ownerName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const generateUPILink = (amount, billId) => {
    return `upi://pay?pa=${adminUPI}&pn=BatataVilla&am=${amount}&tn=Bill_${billId}`;
  };

  const handleGenerateBill = (villa) => {
    setSelectedBill(villa);
    setShowBillDialog(true);
    
    // Also prepare invoice data
    const invoiceData = {
      villaName: villa.villaName,
      ownerName: villa.ownerName,
      ownerEmail: villa.ownerEmail || "",
      ownerPhone: villa.ownerPhone || "",
      villaCity: villa.villaCity || "",
      invoiceNo: `INV-${Date.now().toString().slice(-6)}`,
      dateIssued: formatDate(Date.now()),
      bookings: villa.bookings.map(booking => ({
        bookingId: booking.bookingId,
        guestName: booking.userName || "Guest",
        checkIn: formatDate(booking.checkIn),
        guestCount: booking.guests,
        amount: booking.billAmount
      })),
      upiId: villa.ownerUPI || adminUPI,
      dueDate: formatDate(Date.now() + 10 * 24 * 60 * 60 * 1000), // 10 days from now
      website: "https://www.villakosh.in/",
      email: "contact@villakosh.in",
      phone: "+91 8668737989"
    };
    
    setSelectedInvoiceData(invoiceData);
  };

  const generatePDF = async (villa) => {
    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([400, 600]);
    const { width, height } = page.getSize();
  
    page.drawText(`Bill for ${villa.villaName}`, {
      x: 50,
      y: height - 50,
      size: 20,
      color: rgb(0, 0, 0),
    });
    page.drawText(`Owner: ${villa.ownerName}`, {
      x: 50,
      y: height - 80,
      size: 14,
      color: rgb(0, 0, 0),
    });
    // Replace ₹ with "INR " to avoid encoding issues
    page.drawText(`Total: INR ${villa.totalBill}`, {
      x: 50,
      y: height - 110,
      size: 16,
      color: rgb(0, 0, 0),
    });
  
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${villa.villaName}_bill.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleGenerateInvoice = (villa) => {
    const invoiceData = {
      villaName: villa.villaName,
      ownerName: villa.ownerName,
      ownerEmail: villa.ownerEmail || "",
      ownerPhone: villa.ownerPhone || "",
      villaCity: villa.villaCity || "",
      invoiceNo: `INV-${Date.now().toString().slice(-6)}`,
      dateIssued: formatDate(Date.now()),
      bookings: villa.bookings.map(booking => ({
        bookingId: booking.bookingId,
        guestName: booking.userName || "Guest",
        checkIn: formatDate(booking.checkIn),
        guestCount: booking.guests,
        amount: booking.billAmount
      })),
      upiId: villa.ownerUPI || "admin@upi",
      dueDate: formatDate(Date.now() + 10 * 24 * 60 * 60 * 1000), // 10 days from now
      website: "https://www.villakosh.in/",
      email: "contact@villakosh.in",
      phone: "+91 8668737989"
    };

    setSelectedInvoiceData(invoiceData);
    setShowInvoiceDialog(true);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-100">
        <div className="sticky top-0 bg-white z-50 shadow-sm">
          <div className="flex items-center px-4 py-3">
            <button onClick={handleBack} className="flex items-center text-gray-800">
              <ArrowLeft className="h-6 w-6 mr-2" />
            </button>
            <h1 className="text-xl font-semibold ml-2">
              Monthly Bill: {months[selectedMonth]} {selectedYear}
            </h1>
          </div>
        </div>

        <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search by villa name or owner..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <X className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                </button>
              )}
            </div>

            <div className="flex gap-2">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="border border-gray-300 rounded-md p-2"
              >
                {months.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="border border-gray-300 rounded-md p-2"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="mb-6">
              <div className="bg-gray-100 p-4 rounded-lg">
                <h3 className="text-lg font-semibold">Total Bill Amount</h3>
                <p className="text-2xl font-bold">₹{totalBillAmount}</p>
                <p className="text-xs text-gray-500">Based on ₹100 per guest</p>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : filteredVillas.length > 0 ? (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredVillas.map((villa) => (
                  <Card key={villa.villaName} className="hover:shadow-md transition-shadow">
                    <CardHeader>
                      <CardTitle>{villa.villaName}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-500">Owner: {villa.ownerName}</p>
                      <p className="text-lg font-semibold mt-2">Total Bill: ₹{villa.totalBill}</p>
                      <div className="flex flex-col gap-2 mt-4">
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => setShowDetails(villa)}
                        >
                          View Billing Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">
                  No villas found with bookings for {months[selectedMonth]} {selectedYear}
                </p>
              </div>
            )}

            {showDetails && (
              <div className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">
                    Billing Details for {showDetails.villaName}
                  </h2>
                  <Button variant="outline" onClick={() => setShowDetails(null)}>
                    Back to Villas
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Booking ID
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Guest
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Check-in
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Guest Count
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount (₹100/guest)
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {showDetails.bookings.map((booking) => (
                        <tr key={booking.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            #{booking.bookingId?.slice(-6) || "000000"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {booking.userName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(booking.checkIn)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {booking.guests}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            ₹{booking.billAmount}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="4" className="px-6 py-4 text-right font-semibold">
                          Total
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          ₹{showDetails.totalBill}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="5" className="px-6 py-4">
                          <div className="flex justify-end space-x-4">
                            <Button
                              variant="outline"
                              onClick={() => setShowDetails(null)}
                            >
                              Back to Villas
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => setShowInvoiceDialog(true)}
                            >
                              Generate Invoice
                            </Button>
                            <Button
                              variant="default"
                              onClick={() => handleGenerateBill(showDetails)}
                            >
                              Generate Bill
                            </Button>
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      <Dialog open={showBillDialog} onOpenChange={setShowBillDialog}>
  <DialogContent className="bg-white"> {/* Add bg-white class */}
    <DialogHeader>
      <DialogTitle>Generated Bill</DialogTitle>
    </DialogHeader>
    {selectedBill && (
      <div className="p-4">
        <div className="text-center mb-4">
          <h2 className="text-xl font-bold mb-2">{selectedBill.villaName}</h2>
          <p className="text-gray-600">Owner: {selectedBill.ownerName}</p>
          <p className="text-xs text-gray-500 mt-1">UPI: {selectedBill.ownerUPI || adminUPI}</p>
          <p className="text-2xl font-bold mt-4">₹{selectedBill.totalBill}</p>
        </div>
        
        <div className="flex justify-center mb-4">
          <QRCodeSVG 
            value={generateUPILink(selectedBill.totalBill, selectedBill.villaName)}
            size={200}
            level="H"
            includeMargin={true}
          />
        </div>

        <div className="text-center text-sm text-gray-500">
          <p>Scan QR code to pay</p>
        </div>
      </div>
    )}
    <DialogFooter>
      <Button variant="outline" onClick={() => setShowBillDialog(false)}>
        Close
      </Button>
      <Button 
        variant="default"
        onClick={async () => {
          if (selectedBill) {
            await generatePDF(selectedBill);
          }
          setShowBillDialog(false);
        }}
      >
        Download PDF
      </Button>
    </DialogFooter>
  </DialogContent>
</Dialog>

<Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
  <DialogContent className="max-w-[95vw] md:max-w-5xl p-4 overflow-hidden">
    <DialogHeader>
      <DialogTitle>Generated Invoice</DialogTitle>
    </DialogHeader>
    <div className="overflow-x-auto max-h-[80vh]">
      {selectedInvoiceData && (
        <InvoiceGenerator data={selectedInvoiceData} />
      )}
    </div>
    <DialogFooter>
      <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>
        Close
      </Button>
      <Button 
        variant="default"
        onClick={() => {
          setShowInvoiceDialog(false);
          setShowBillDialog(true);
        }}
      >
        Show QR Code
      </Button>
    </DialogFooter>
  </DialogContent>
</Dialog>
    </Layout>
  );
}